import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Modal, Select, Stack } from '@mantine/core';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import { useTranslation } from 'react-i18next';

import {
    createUserRole,
    deleteUserRole,
    listRoles,
    listUserRoles,
    listUsers,
} from '../../api/resources';
import { DetailDrawer } from '../../components/detail/DetailDrawer';
import { UserRoleDetailPanel } from '../../components/detail/UserRoleDetailPanel';
import { EntityRef } from '../../components/EntityRef';
import { PageHeader } from '../../components/PageHeader';
import { PaginatedTable } from '../../components/PaginatedTable';
import { RemoveEntityAction } from '../../components/RemoveEntityAction';
import { useAdminLookups } from '../../hooks/useAdminLookups';
import { DEFAULT_PAGE_SIZE, useListQuery } from '../../hooks/useListQuery';
import type { UserRole } from '../../types/api';

export function UserRolesPage() {
    const { t } = useTranslation();
    const [page, setPage] = useState(1);
    const [detail, setDetail] = useState<UserRole | null>(null);
    const [createOpen, setCreateOpen] = useState(false);
    const queryClient = useQueryClient();
    const lookups = useAdminLookups();
    const { data, isLoading } = useListQuery('user-role', listUserRoles, {
        page,
        scope: 'platform',
    });

    const rolesQuery = useQuery({
        queryKey: ['roles-platform'],
        queryFn: () => listRoles({ limit: 100, page: 1, scope: 'platform' }),
    });
    const usersQuery = useQuery({
        queryKey: ['users-all'],
        queryFn: () => listUsers({ limit: 200, page: 1 }),
    });

    const platformRoles = useMemo(() => rolesQuery.data?.data ?? [], [rolesQuery.data]);

    const form = useForm({ initialValues: { userId: '', roleId: '' } });

    const createMutation = useMutation({
        mutationFn: () => createUserRole(form.values),
        onSuccess: () => {
            notifications.show({ color: 'green', message: t('features.userRoles.assigned') });
            setCreateOpen(false);
            void queryClient.invalidateQueries({ queryKey: ['user-role'] });
        },
        onError: (e: Error) => notifications.show({ color: 'red', message: e.message }),
    });

    return (
        <Stack gap="lg">
            <PageHeader
                title={t('features.userRoles.title')}
                description={t('features.userRoles.description')}
                actions={
                    <Button onClick={() => setCreateOpen(true)}>
                        {t('features.userRoles.assignAction')}
                    </Button>
                }
            />
            <PaginatedTable
                rowKey={(r) => r.id}
                columns={[
                    {
                        key: 'user',
                        header: t('fields.user'),
                        render: (r) => {
                            const u = lookups.resolveUser(r.userId, r.user);
                            return (
                                <EntityRef
                                    title={u.title}
                                    id={u.id}
                                    secondary={u.secondary}
                                    href={`/users?id=${r.userId}`}
                                />
                            );
                        },
                    },
                    {
                        key: 'role',
                        header: t('fields.role'),
                        render: (r) => {
                            const role = lookups.resolveRole(r.roleId, r.role);
                            return <EntityRef title={role.title} id={role.id} />;
                        },
                    },
                ]}
                rows={data?.data ?? []}
                total={data?.total ?? 0}
                page={page}
                pageSize={DEFAULT_PAGE_SIZE}
                onPageChange={setPage}
                isLoading={isLoading}
                onRowClick={setDetail}
            />
            <DetailDrawer
                opened={Boolean(detail)}
                onClose={() => setDetail(null)}
                title={t('detail.userRole')}
                footer={
                    detail ? (
                        <RemoveEntityAction
                            entityId={detail.id}
                            entityName={(() => {
                                const user = lookups.resolveUser(detail.userId, detail.user);
                                const role = lookups.resolveRole(detail.roleId, detail.role);
                                return `${user.title} — ${role.title}`;
                            })()}
                            deleteFn={deleteUserRole}
                            queryKeys={['user-role']}
                            successMessageKey="features.userRoles.removed"
                            actionLabelKey="common.actions.removeAssignment"
                            onRemoved={() => setDetail(null)}
                        />
                    ) : undefined
                }
            >
                {detail ? <UserRoleDetailPanel assignment={detail} /> : null}
            </DetailDrawer>
            <Modal
                opened={createOpen}
                onClose={() => setCreateOpen(false)}
                title={t('features.userRoles.createModalTitle')}
            >
                <Stack>
                    <Select
                        label={t('fields.user')}
                        searchable
                        data={(usersQuery.data?.data ?? []).map((u) => ({
                            value: u.id,
                            label: `${u.firstName} ${u.lastName} (${u.email ?? ''})`,
                        }))}
                        value={form.values.userId}
                        onChange={(v) => form.setFieldValue('userId', v ?? '')}
                    />
                    <Select
                        label={t('features.userRoles.platformRole')}
                        data={platformRoles.map((r) => ({
                            value: r.id,
                            label: r.name,
                        }))}
                        value={form.values.roleId}
                        onChange={(v) => form.setFieldValue('roleId', v ?? '')}
                    />
                    <Button loading={createMutation.isPending} onClick={() => createMutation.mutate()}>
                        {t('common.actions.assign')}
                    </Button>
                </Stack>
            </Modal>
        </Stack>
    );
}
